import type { QueryInput } from "@/models/compound";
import type { QueryProgressData } from "@/warehouse/QueryData";
import { QueryData } from "@/warehouse/QueryData";

// Base class to implement a new Loading Strategy to get QueryInputs objects
export abstract class LoadingStrategy {
  /**
   * Returns a max given number of QueryInput items based on the implemented strategy
   * @param identifier Defines the source of the data stored in the warehouse
   * @param maxObjectReturn Defines the max number of QueryInput items to be returned, if possible
   * @param onlyNewElements Flag that indicates that we only want to retrieve clean unstarted filenames.
   * @returns Array of QueryInput objects with the strategy applied to fetch them
   */
  abstract getQueryInputs(
    identifier: string,
    maxObjectReturn: number,
    onlyNewElements: boolean
  ): QueryInput[];

  /**
   * Wrapper to get the warehouse data without the suffix of the identifier, if any
   * @param identifier Defines the source of the data stored in the warehouse
   * @returns The stored QueryData or undefined
   */
  getWarehouseData(identifier: string): QueryData | undefined {
    const trimmedIdentifier = identifier.split("_")[0];
    return QueryData.getQueryData(trimmedIdentifier);
  }

  /**
   * Get the list of new and started/in progress filenames from the warehouse progress object
   * @param warehouseProgress Base object that contains the filenames and progress
   * @returns Two arrays with the new and started or in progress filenames
   */
  getNewAndStartedFilenames(
    warehouseProgress: Map<string, QueryProgressData>
  ): {
    newFilenames: string[];
    startedFilenames: string[];
  } {
    const newFilenames = [];
    const startedFilenames = [];
    for (const filename of warehouseProgress.keys()) {
      const filenameProgress = warehouseProgress.get(
        filename
      ) as QueryProgressData;
      if (filenameProgress.state === "new") newFilenames.push(filename);
      else if (filenameProgress.state === "started")
        startedFilenames.push(filename);
    }
    return { newFilenames, startedFilenames };
  }

  /**
   * Creates and returns a new QueryInput object for all the given filenames.
   * It also updates the QueryData progress state to reflect these changes.
   * @param identifier Defines the source of the data stored in the warehouse
   * @param warehouseData Stored warehouse data for the given identifier
   * @param maxObjects Max amount of QueryInput objects to return
   * @param filenames List of filenames from which the QueryInput object will be created
   * @returns Array of QueryInput objects created from the filenames
   */
  createQueryInputObjects(
    identifier: string,
    warehouseData: QueryData,
    maxObjects: number,
    filenames: string[]
  ): QueryInput[] {
    // const trimmedIdentifier = identifier.split("_")[0];
    const queryInputToReturn: QueryInput[] = filenames
      .slice(0, maxObjects)
      .map((filename) =>
        QueryData.createQueryInput(
          identifier,
          warehouseData.sharedInput,
          filename,
          (warehouseData.progress.get(filename) as QueryProgressData).pageCursor
        )
      );

    const trimmedIdentifier = identifier.split("_")[0];
    queryInputToReturn.forEach((queryInput) =>
      QueryData.updateQueryProgressState(
        trimmedIdentifier,
        queryInput.filename,
        "inProgress"
      )
    );

    return queryInputToReturn;
  }
}
